<template>
  <section ref="root" class="scroll-mt-20 bg-white px-4 focus:outline-none md:px-10" tabindex="-1" :aria-labelledby="isAccordion ? headingId : undefined" :class="isExpanded ? 'py-8' : 'py-4'">
    <div v-if="isAccordion && isExpanded" class="mb-5">
      <h3 class="tracking-wide md:text-xl">
        <!-- 開いたセクションは Continue でしか畳まないため、押せない開示ボタンとして開いていることだけを伝える（WAI-ARIA の accordion パターン） -->
        <button :id="headingId" aria-disabled="true" aria-expanded="true" class="w-full cursor-default text-left" type="button" :aria-controls="`section-${sectionId}`">{{ title }}</button>
      </h3>
    </div>
    <template v-else-if="isAccordion">
      <h3 v-if="isVisited" class="tracking-wide">
        <button
          :id="headingId"
          aria-expanded="false"
          class="flex w-full items-center gap-x-3 text-left"
          type="button"
          :aria-controls="`section-${sectionId}`"
          :data-testid="`edit-${sectionId}`"
          @click="onClickEdit"
        >
          <!-- 必須が空になったセクションは ✓ を落とす。幅は空でも確保してタイトルの位置を揃える -->
          <span aria-hidden="true" class="w-4 text-gold1">{{ isFulfilled ? '✓' : '' }}</span>
          <span class="flex-1">{{ title }}</span>
          <span class="text-sm text-gold3 underline underline-offset-2">Edit</span>
        </button>
      </h3>
      <h3 v-else :id="headingId" class="tracking-wide text-beige4">{{ title }}</h3>
    </template>
    <Transition :css="false" @after-enter="onAfterEnter" @enter="onEnter" @leave="onLeave">
      <div v-show="isExpanded" :id="`section-${sectionId}`" :data-testid="`section-${sectionId}`">
        <slot />
        <AtomsPrimaryButton v-if="isAccordion && hasContinueButton" class="mt-8 w-full" :data-testid="`continue-${sectionId}`" :disabled="!isFulfilled" :on-click="onClickContinue">Continue</AtomsPrimaryButton>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import type { ReservationAccordionController } from '@/wabunka-src/common/features/Reservation/composables/accordion/useReservationAccordion';
import type { ReservationSectionId } from '@/wabunka-src/common/features/Reservation/core/requiredFields/sections';
import { sendGAWabunkaReservationSectionContinueEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/reservationSectionContinue';
import { sendGAWabunkaReservationSectionEditEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/reservationSectionEdit';

interface Props {
  sectionId: ReservationSectionId;
  title: string;
  /** 新動線のときだけアコーディオンとして畳む。旧動線では常に開いた 1 枚のカードとして描画する */
  isAccordion: boolean;
  accordion: ReservationAccordionController;
  /** 必須が埋まっているか。✓ の表示と Continue の活性に使う。payment は判定を持たない（D-012） */
  isFulfilled: boolean;
  /** S4 は Continue を持たず、既存の送信ボタンで進む */
  hasContinueButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), { hasContinueButton: false });

const COLLAPSE_DURATION_MS = 240;

const root = ref<HTMLElement | null>(null);

const headingId = computed(() => `section-heading-${props.sectionId}`);

const isOpen = computed(() => props.accordion.isOpen(props.sectionId));
const isExpanded = computed(() => !props.isAccordion || isOpen.value);
// 一度通過したセクションは、必須が空になっても開き直せるようにしておく（✓ だけ落とす）
const isVisited = computed(() => props.accordion.isVisited(props.sectionId));

const onClickEdit = () => {
  sendGAWabunkaReservationSectionEditEvent(props.sectionId);
  props.accordion.openSection(props.sectionId);
};
const onClickContinue = () => {
  sendGAWabunkaReservationSectionContinueEvent(props.sectionId);
  props.accordion.completeSection(props.sectionId);
};

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

// 高さ auto は補間できないため、開閉のたびに実測値へ差し替えてアニメーションさせる。
// 折りたたみ中だけ overflow を切るのは、開いている間に SelectBox のドロップダウンを刈らないため。
const animateHeight = (element: Element, from: number, to: number, done: () => void) => {
  const node = element as HTMLElement;
  if (typeof node.animate !== 'function' || prefersReducedMotion()) {
    done();
    return;
  }

  node.style.overflow = 'hidden';
  const animation = node.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: COLLAPSE_DURATION_MS, easing: 'ease' });
  const finish = () => {
    node.style.overflow = '';
    done();
  };
  animation.onfinish = finish;
  animation.oncancel = finish;
};

const onEnter = (element: Element, done: () => void) => animateHeight(element, 0, element.scrollHeight, done);
const onLeave = (element: Element, done: () => void) => animateHeight(element, element.scrollHeight, 0, done);

// 畳む側の高さが消えるまで待ってから位置を合わせる。開いた瞬間に合わせると、
// その後に縮んだ分（畳まれるセクションの高さ）だけ行き過ぎる
const onAfterEnter = () => {
  if (!props.isAccordion) return;
  requestAnimationFrame(() => root.value?.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' }));
};

// Edit ボタンは開くと同時に消えるため、フォーカスを引き取らないとキーボード操作の位置が失われる
watch(isOpen, (opened) => {
  if (!opened || !props.isAccordion) return;
  root.value?.focus?.({ preventScroll: true });
});
</script>
